import React, { useMemo, useState, useEffect } from 'react'
import { H3, Subheading, Separator, Button, InfoSheetPanel, Row, Overline, usePageWidth } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useFeatureFlag } from '@fs/zion-flags'
import ZionForm, { useZionForm, FormAutosuggest, useWatch } from '@fs/zion-form'
import { css } from '@emotion/core'
import {
  getNestedElementById,
  getPersonName,
  useSlsDispatchContext,
  useSlsHighlightsContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import { useFamilyReviewDispatchContext, useFamilyReviewStateContext, STEPS } from '../../providers/FamilyReviewContext'
import HighlightButton from '../highlightButton/HighlightButton'
import { getAllPersons, getEventTypeSuggestions, getPrimaryOrFirstPerson } from '../../utils/utils'
import { showEventsHighlights } from '../../utils/highlightUtils'
import { saveNewEvent } from '../../utils/saveFunctions'

// we need this because the Autosuggest has a margin where we want a button
const noBottomMarginCss = css`
  & div {
    margin-bottom: 0px;
  }
`

export default function AddNewEvent({
  currentTask,
  addNewEventOverlay,
  editHighlightsOverlay,
  editEventOverlay,
  setSaveUpdateCalled,
}) {
  const h = useSlsHighlightsContext()
  const recordId = currentTask?.id
  const dispatch = useFamilyReviewDispatchContext()
  const saveChangesFlag = useFeatureFlag('rip_sharedImageViewer_saveChanges')
  const zionSlsDispatch = useSlsDispatchContext()
  const zionSLSState = useSlsStateContext()
  const state = useFamilyReviewStateContext()
  const [t] = useTranslation()
  const atPageWidth = usePageWidth()
  const isSmallScreen = atPageWidth({ default: true, md: false })
  const events = currentTask.subElements
    ?.filter((se) => se.elementType === 'PERSON')
    .flatMap((person) => person?.subElements?.filter((el) => el.elementType === 'EVENT'))
  const recordHasPrimaryEvent = Boolean(events.find((event) => event?.typeData?.primaryOnly))
  const [isEventPrimary, setIsEventPrimary] = useState(!recordHasPrimaryEvent)
  const primaryPerson = getPrimaryOrFirstPerson(currentTask)

  const methods = useZionForm({
    mode: 'all',
    defaultValues: {
      type: { key: '', primaryText: '' },
      person: { key: '', primaryText: '', isPrimary: false },
    },
    resolver: (values) => {
      const errors = {}
      methods.clearErrors()
      const { type, person } = values
      if (!type?.key)
        errors.type = { type: 'required', message: t('FormValidation.notEmpty', 'Field cannot be empty.') }
      if (!person?.key)
        errors.person = { type: 'required', message: t('FormValidation.notEmpty', 'Field cannot be empty.') }
      return { values, errors }
    },
  })

  const [type, person] = useWatch({
    name: ['type', 'person'],
    control: methods.control,
  })

  const handleSubmit = async () => {
    const standardType = zionSLSState.types?.events?.find((e) => e.eventType === type.key)
    type.fieldId = state.addNewEvent.typeId
    await saveNewEvent({
      eventId: state.addNewEvent.eventId,
      highlights: [...h.getHighlights().mainEditHighlights, ...h.getHighlights().splitScreenEditHighlights],
      isEventPrimary,
      partitionKey: currentTask.partitionKey,
      personId: person.key,
      saveChangesFlag,
      type: standardType,
      typeFieldId: state.addNewEvent.typeId,
      userId: state.userId,
      recordId,
      zionSLSState,
      zionSlsDispatch,
    })
    setSaveUpdateCalled(true)
    dispatch({ type: 'SET_SELECTED_EVENT_ID', value: state.addNewEvent.eventId })
  }

  const resetHighlights = () => {
    showEventsHighlights({
      h,
      currentTask,
      highlights: [...h.getHighlights().mainEditHighlights, ...h.getHighlights().splitScreenEditHighlights],
      eventIds: [state.stepThree.eventCards.find((card) => !card.hasBeenReviewed)?.eventId], // set highlights to next event to be reviewed
      isSmallScreen,
      splitScreenPartitionKey: state.splitScreenPartitionKey,
    })
  }

  const resetState = () => {
    const nextEventCard = state.stepThree.eventCards.find((cardState) => !cardState.hasBeenReviewed)
    if (nextEventCard) {
      dispatch({
        type: 'SET_SELECTED_EVENT_ID',
        value: nextEventCard.eventId,
      })
    }
  }

  const closeAddNewEvent = () => {
    methods.reset()
    dispatch({ type: 'RESET_ADD_NEW_EVENT_IDS' })
    addNewEventOverlay.close()
  }

  const saveButtonClicked = async () => {
    await handleSubmit()
    closeAddNewEvent()
    editEventOverlay.open()
  }

  const cancelButtonButtonClicked = () => {
    closeAddNewEvent()
    resetHighlights()
    resetState()
  }

  // The back arrow on the panel was clicked
  const userClosedPanel = () => {
    closeAddNewEvent()
    resetHighlights()
    resetState()
  }

  const personSuggestions = getAllPersons(currentTask).map((personElement) => ({
    key: personElement?.id,
    primaryText: getPersonName({ person: personElement, justOneName: true }),
    isPrimary: personElement?.primary ?? false,
  }))

  const eventTypeSuggestions = useMemo(() => {
    const primaryPersonSelected = person && person?.key === primaryPerson?.id
    const primaryEventType = state.stepThree.eventCards
      .map((cardState) => getNestedElementById(currentTask?.subElements, cardState.eventId))
      .filter((event) => event?.primary)[0]?.eventType
    return getEventTypeSuggestions({
      types: zionSLSState.types,
      t,
      isPrimary: isEventPrimary,
      primaryEventType: primaryPersonSelected ? primaryEventType : null,
    })
  }, [
    t,
    zionSLSState.types,
    isEventPrimary,
    currentTask?.subElements,
    state.stepThree.eventCards,
    person,
    primaryPerson?.id,
  ])

  const handleHighlightButtonClicked = (id) => {
    if (id) {
      dispatch({ type: 'SET_ACTIVE_FIELD_ID', value: id })
      h.setDisplayHighlights({
        mainPartitionKey: currentTask.partitionKey,
        splitScreenPartitionKey: state.splitScreenPartitionKey,
        setMainZoom: true,
        setSplitScreenZoom: Boolean(state.splitScreenPartitionKey),
        attachFilter: (hl) => hl.fieldId === id,
        mainZoomFilter: (hl) => hl.fieldId === id,
        splitScreenZoomFilter: (hl) => hl.fieldId === id,
        isSmallScreen,
      })
    }
  }
  const getTitle = () => {
    if (state.currentStep === STEPS.STEP_THREE) return t('family-review.step4.heading', 'Life Events')
    return t('family-review.step5.heading', 'Final Review')
  }

  useEffect(() => {
    if (type?.key && !eventTypeSuggestions?.find((suggestion) => suggestion.key === type.key)) {
      methods.setValue('type', { key: '', primaryText: '' })
    }
  }, [eventTypeSuggestions, type, methods])

  // This useEffect is necessary to set the default value when coming from the Add Floating Event panel
  // and we want to keep the person field filled in.
  useEffect(() => {
    const defaultPersonId = state.stepThree.addedFloatingEventPersonId
    let isMounted = true

    if (defaultPersonId && person?.key === '') {
      const defaultPerson = getNestedElementById(currentTask.subElements, defaultPersonId)
      const defaultPersonName = defaultPerson ? getPersonName({ person: defaultPerson, justOneName: true }) : ''
      if (isMounted) {
        methods.setValue('person', {
          key: defaultPersonId,
          primaryText: defaultPersonName,
          isPrimary: defaultPerson?.primary ?? false,
        })
      }
    }

    return () => {
      isMounted = false
    }
  }, [state.stepThree.addedFloatingEventPersonId, currentTask.subElements, methods, person])

  return (
    <InfoSheetPanel
      {...{ ...addNewEventOverlay, close: userClosedPanel }}
      title={getTitle()}
      headingColor="green"
      footer={
        <Row alignX="end">
          <Button
            onClick={cancelButtonButtonClicked}
            linkName="New Event Cancel"
            data-testid="add-new-event-cancel-button"
          >
            {t('edit-index.cancel.button', 'Cancel')}
          </Button>
          <Button
            emphasis="high"
            linkName="New Event Save"
            data-testid="add-new-event-save-button"
            form="add-new-event"
            type="submit"
          >
            {t('edit-index.save.button', 'Save')}
          </Button>
        </Row>
      }
    >
      {addNewEventOverlay.isOpen && (
        <>
          <H3 size="H5">
            <Overline>
              {t('family-review.stepXof4', 'Step {step} of 4', {
                step: state.currentStep === STEPS.STEP_THREE ? '3' : '4',
              })}
            </Overline>
            {t('family-review.addEvent.heading', 'Add New Event')}
            <Subheading>
              {t(
                'family-review.addEvent.content',
                'Select a person from the dropdown, select an event type, and add any new fields.'
              )}
            </Subheading>
          </H3>
          <Separator />
          <ZionForm methods={methods} onSubmit={saveButtonClicked} id="add-new-event">
            <FormAutosuggest
              data-testid="person-autosuggest"
              clearOnClickAway
              clearable
              id="event-person"
              name="person"
              label={t('family-review.addEvent.formLabel.person', 'Event Person')}
              placeholder={t('family-review.addEvent.formLabel.person', 'Event Person')}
              suggestions={personSuggestions}
              onSuggestionSelected={(suggestion) => {
                const canEventBePrimary = !recordHasPrimaryEvent && suggestion?.isPersonPrimary
                setIsEventPrimary(canEventBePrimary)
              }}
            />
            <span css={noBottomMarginCss}>
              <FormAutosuggest
                data-testid="type-autosuggest"
                clearOnClickAway
                clearable
                id="event-type"
                name="type"
                label={t('family-review.addEvent.formLabel.type', 'Event Type')}
                placeholder={t('family-review.addEvent.formLabel.type', 'Event Type')}
                suggestions={eventTypeSuggestions}
              />
            </span>
            <HighlightButton
              isAdding={
                !h
                  .getHighlights()
                  .mainEditHighlights.find((highlight) => highlight.fieldId === state.addNewEvent.typeId)
              }
              editHighlightsOverlay={editHighlightsOverlay}
              handleHighlightButtonClicked={() => handleHighlightButtonClicked(state.addNewEvent.typeId)}
            />
          </ZionForm>
        </>
      )}
    </InfoSheetPanel>
  )
}
